/**
 * IntentSignalsPanel — surfaces real-time signals (news, funding, hiring)
 * about a lead's company via Perplexity, then lets the user generate a
 * hyper-personalized cold email in one click.
 *
 * Renders inline inside SequenceModal. On "Apply to email", the parent
 * modal receives the generated {subject, body} via the `onApply` callback
 * and overwrites its own state.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Radar, Sparkles, Loader2, Newspaper, DollarSign, Users, Rocket,
  ExternalLink, RefreshCw, Wand2, ChevronDown, ChevronUp, AlertTriangle,
} from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IntentSignalsPanel({ lead, onApply, senderName, valueProp }) {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [status, setStatus] = useState({ loading: true, configured: false });
  const [signals, setSignals] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [writing, setWriting] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/intent-signals/status`)
      .then(({ data }) => active && setStatus({ loading: false, ...data }))
      .catch(() => active && setStatus({ loading: false, configured: false }));
    // Preload any previously cached signals on the lead.
    if (lead?.intent_signals) setSignals(lead.intent_signals);
    return () => { active = false; };
  }, [lead?.id, lead?.intent_signals]);

  if (!lead) return null;

  const scan = async (refresh = false) => {
    setScanning(true);
    try {
      const { data } = await axios.post(
        `${API}/intent-signals/leads/${lead.id}/scan${refresh ? "?refresh=1" : ""}`,
        {}, { timeout: 90000 }
      );
      setSignals(data.signals);
      toast.success(data.cached
        ? (isPt ? "Sinais em cache carregados" : "Loaded cached signals")
        : (isPt ? "Sinais atualizados via Perplexity" : "Signals refreshed via Perplexity"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setScanning(false); }
  };

  const personalize = async () => {
    setWriting(true);
    try {
      const { data } = await axios.post(
        `${API}/intent-signals/leads/${lead.id}/personalize`,
        {
          lang: isPt ? "pt" : "en",
          tone: "friendly",
          sender_name: senderName || "",
          value_prop: valueProp || "",
        }, { timeout: 60000 });
      onApply && onApply({ subject: data.subject, body: data.body, opener: data.opener });
      toast.success(isPt ? "E-mail personalizado gerado" : "Personalized email generated");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        toast.info(isPt ? "Rode a varredura primeiro" : "Run the scan first");
      } else {
        toast.error(e?.response?.data?.detail || e.message);
      }
    } finally { setWriting(false); }
  };

  if (status.loading) {
    return (
      <div className="rounded-lg border border-[#E5E7EB] bg-[#FAFAFB] p-3 text-[12px] text-[#6B7280] inline-flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> {isPt ? "Checando Perplexity…" : "Checking Perplexity…"}
      </div>
    );
  }

  if (!status.configured) {
    return (
      <div data-testid="intent-panel-missing-key"
        className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <b>Perplexity {isPt ? "não configurado" : "not configured"}.</b>{" "}
          {isPt ? "Ative a chave em Master → Integrations para habilitar Intent Signals."
                : "Add the API key under Master → Integrations to unlock Intent Signals."}
        </div>
      </div>
    );
  }

  const hasSignals = !!signals;
  const hook = signals?.golden_hook || "";
  const news = signals?.recent_news || [];
  const funding = signals?.funding || [];
  const hiring = signals?.hiring || [];
  const launches = signals?.product_launches || [];

  return (
    <div data-testid="intent-signals-panel"
      className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-violet-50 p-3.5 space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-indigo-600 text-white flex items-center justify-center">
          <Radar className="w-3.5 h-3.5" />
        </div>
        <div className="leading-tight flex-1 min-w-0">
          <div className="text-[12.5px] font-bold text-indigo-900 inline-flex items-center gap-1.5">
            Intent Signals
            <span className="text-[9.5px] font-bold uppercase tracking-wider text-indigo-600 bg-white px-1.5 py-0.5 rounded border border-indigo-200">
              Perplexity
            </span>
          </div>
          <div className="text-[10.5px] text-indigo-700/70">
            {isPt ? "Notícias, funding, vagas e Golden Hook em tempo real."
                  : "Real-time news, funding, hiring and Golden Hook."}
          </div>
        </div>
        <button type="button" onClick={() => setExpanded(!expanded)}
          className="w-7 h-7 rounded-md hover:bg-white/60 inline-flex items-center justify-center text-indigo-700"
          data-testid="intent-panel-toggle">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {expanded && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            {!hasSignals ? (
              <button type="button" onClick={() => scan(false)} disabled={scanning}
                data-testid="intent-scan-btn"
                className="h-8 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[11.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
                {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Radar className="w-3.5 h-3.5" />}
                {scanning ? (isPt ? "Varrendo…" : "Scanning…") : (isPt ? "Varrer sinais" : "Scan signals")}
              </button>
            ) : (
              <>
                <button type="button" onClick={personalize} disabled={writing}
                  data-testid="intent-personalize-btn"
                  className="h-8 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[11.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
                  {writing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                  {writing ? (isPt ? "Escrevendo…" : "Writing…") : (isPt ? "Gerar e-mail" : "Generate email")}
                </button>
                <button type="button" onClick={() => scan(true)} disabled={scanning}
                  data-testid="intent-refresh-btn"
                  className="h-8 px-2.5 rounded-lg bg-white hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[11px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
                  {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  {isPt ? "Atualizar" : "Refresh"}
                </button>
              </>
            )}
            {hasSignals && signals.scanned_at && (
              <span className="text-[10px] text-indigo-700/70 ml-auto">
                {isPt ? "Atualizado em " : "Updated "}
                {new Date(signals.scanned_at).toLocaleString(isPt ? "pt-BR" : "en-US",
                  { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>

          {hook && (
            <div data-testid="intent-golden-hook"
              className="rounded-lg bg-white border border-indigo-200 p-3">
              <div className="text-[9.5px] uppercase tracking-wider font-bold text-indigo-600 mb-1 inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Golden Hook
              </div>
              <p className="text-[12.5px] text-indigo-950 font-medium leading-snug">{hook}</p>
            </div>
          )}

          {hasSignals && (
            <div className="grid grid-cols-2 gap-2 text-[11.5px]">
              <SignalChip icon={Newspaper} label={isPt ? "Notícias" : "News"} count={news.length} tone="indigo" />
              <SignalChip icon={DollarSign} label="Funding" count={funding.length} tone="emerald" />
              <SignalChip icon={Users} label={isPt ? "Vagas" : "Hiring"} count={hiring.length} tone="sky" />
              <SignalChip icon={Rocket} label={isPt ? "Lançamentos" : "Launches"} count={launches.length} tone="amber" />
            </div>
          )}

          {news.length > 0 && (
            <details className="text-[12px]" data-testid="intent-news-details">
              <summary className="cursor-pointer font-semibold text-indigo-800 inline-flex items-center gap-1">
                <Newspaper className="w-3.5 h-3.5" /> {isPt ? "Notícias recentes" : "Recent news"} ({news.length})
              </summary>
              <ul className="mt-2 space-y-1.5">
                {news.slice(0, 3).map((n, i) => (
                  <li key={`news-${n.url || i}`} className="pl-2 border-l-2 border-indigo-200">
                    <a href={n.url || "#"} target="_blank" rel="noopener noreferrer"
                      className="font-semibold text-indigo-900 hover:underline inline-flex items-start gap-1">
                      {n.headline} {n.url && <ExternalLink className="w-3 h-3 mt-0.5 shrink-0" />}
                    </a>
                    {n.summary && <p className="text-[11px] text-indigo-950/70 mt-0.5">{n.summary}</p>}
                    {n.date && <span className="text-[10px] text-indigo-600/70">{n.date}</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {signals?.pain_points?.length > 0 && (
            <details className="text-[12px]" data-testid="intent-pain-details">
              <summary className="cursor-pointer font-semibold text-indigo-800 inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> {isPt ? "Pontos de dor" : "Pain points"} ({signals.pain_points.length})
              </summary>
              <ul className="mt-2 space-y-1 list-disc list-inside text-[11.5px] text-indigo-950/80">
                {signals.pain_points.map((p, i) => <li key={`pain-${i}`}>{p}</li>)}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}

function SignalChip({ icon: Icon, label, count, tone }) {
  const toneMap = {
    indigo: "bg-indigo-100 text-indigo-800 border-indigo-200",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    sky: "bg-sky-100 text-sky-800 border-sky-200",
    amber: "bg-amber-100 text-amber-800 border-amber-200",
  };
  return (
    <div className={`rounded-md border px-2 py-1.5 inline-flex items-center gap-1.5 ${toneMap[tone]}`}>
      <Icon className="w-3.5 h-3.5" />
      <span className="font-semibold">{label}</span>
      <span className="ml-auto font-bold tabular-nums">{count}</span>
    </div>
  );
}
